import { useCallback } from 'react'
import { IShipperRatesSortByEnum } from 'types/graphqlTypes'
import { useGraphQLPagination } from 'utils/hooks/useGraphQLPagination'
import useUserDetails from 'utils/hooks/useUserDetails'
import { ShipperRatesFilters } from 'apps/marketplace/apiTypes/shipperRates'
import { ShipperRateObjectAugmented } from 'apps/marketplace/utils/hooks/AwardedLanes/useAwardedLanesApi'
import { SortOrder } from '../../shippers/utils/types/shipper'
import { IAwardedLaneFragment } from '../graphql/AwardedLaneFragment'
import {
  IAwardedLanesQuery,
  IAwardedLanesQueryVariables,
  useAwardedLanesQuery,
} from '../graphql/AwardedLanes'
import { getAugmentedRate } from '../utils/getAugmentedRate'
import { getAwardedLanesQueryVariables } from '../utils/getAwardedLanesQueryVariables'

const PER_PAGE = 50

interface IUseAwardedLanesProps {
  filters: ShipperRatesFilters
  sortField: IShipperRatesSortByEnum
  sortOrder: SortOrder
}

interface IUseAwardedLanesState {
  awardedLanes: ShipperRateObjectAugmented[]
  currentPage: number
  loading: boolean
  totalCount: number
  totalPages: number
  handlePageChange: (pageNumber: number) => Promise<void>
  handleFetchFirstPage: () => void
}

export const useAwardedLanes = ({
  filters,
  sortField,
  sortOrder,
}: IUseAwardedLanesProps): IUseAwardedLanesState => {
  const { userDetails } = useUserDetails()
  const { id: currentUserId } = userDetails || {}

  const getQueryVariables = useCallback((): IAwardedLanesQueryVariables => {
    if (filters) {
      return getAwardedLanesQueryVariables({ currentUserId, filters, sortField, sortOrder })
    }
    return {}
  }, [currentUserId, filters, sortField, sortOrder])

  const { data, fetchMore, loading, refetch } = useAwardedLanesQuery({
    variables: getQueryVariables(),
    notifyOnNetworkStatusChange: true,
  })
  const { shipperRates } = data || {}
  const { edges, pageInfo, totalCount } = shipperRates || {}
  const lanes: IAwardedLaneFragment[] = edges?.map(edge => edge?.node as IAwardedLaneFragment) || []

  const {
    currentPage,
    handlePageChange,
    handleResetPagination,
    paginatedNodes,
    totalPages,
  } = useGraphQLPagination<IAwardedLaneFragment, IAwardedLanesQuery, IAwardedLanesQueryVariables>({
    fetchMore,
    nodes: lanes,
    pageInfo,
    perPage: PER_PAGE,
    totalCount: totalCount || 0,
    variables: getQueryVariables(),
  })

  const awardedLanes: ShipperRateObjectAugmented[] = paginatedNodes.map(lane =>
    getAugmentedRate(lane)
  )

  const handleFetchFirstPage = useCallback(() => {
    handleResetPagination()
    refetch(getQueryVariables())
  }, [getQueryVariables, handleResetPagination, refetch])

  return {
    awardedLanes,
    currentPage,
    loading,
    totalCount: totalCount || 0,
    totalPages,
    handlePageChange,
    handleFetchFirstPage,
  }
}
